import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Layers, Lightbulb, Plus, X } from 'lucide-react';
import { SUBJECTS } from '@jeraisonne/db';
import { Button, cn, MathText, Paginator } from '@jeraisonne/ui';
import {
  CARD_STATUSES,
  CARD_STATUS_LABELS,
  CARD_TYPE_LABELS,
  type CardStatus,
} from '@jeraisonne/db/cards';
import { useBulkCardAction, useCards, type CardRow } from '@/lib/cards/api';
import {
  BULK_CARD_ACTION_LABELS,
  formatBulkSummary,
  planBulkCardAction,
  toggleAll,
  toggleId,
  type BulkCardAction,
} from '@/lib/cards/bulk';
import { formatCardLevels } from '@/features/cards/card-faces';
import { usePagedList } from '@/lib/use-paged-list';
import { filterCards, type CardFilterable, type CardFilters } from '@jeraisonne/db/cards';
import { SkillcardFilters } from '@/features/skillcards/skillcard-filters';
import { TutorLibraryTabs } from '@/components/tutor-library-tabs';
import { isAdmin, useAuth } from '@/lib/auth';
import {
  ADMIN_CARD_SORTS,
  adminCardStatsParts,
  sortCardsByStats,
  useAdminCardStats,
  type AdminCardSort,
  type AdminCardStats,
} from '@/lib/cards/admin-stats';

export const CARD_STATUS_STYLES: Record<CardStatus, string> = {
  draft: 'bg-ink-100 text-ink-600',
  generated: 'bg-primary-50 text-primary-600',
  review: 'bg-accent-50 text-accent-600',
  published: 'bg-xp-100 text-xp-700',
  archived: 'bg-ink-100 text-ink-400',
};

function CardListRow({
  card,
  selected,
  onToggle,
  stats,
}: {
  card: CardRow;
  selected: boolean;
  onToggle: () => void;
  stats?: AdminCardStats | null;
}) {
  return (
    <div className="flex items-center gap-1">
      <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
        <span className="sr-only">Sélectionner la carte {card.title}</span>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="accent-primary-500 h-5 w-5"
        />
      </label>
      <CardListLink card={card} stats={stats} />
    </div>
  );
}

function CardListLink({ card, stats }: { card: CardRow; stats?: AdminCardStats | null }) {
  const statsParts = stats === undefined ? null : adminCardStatsParts(stats ?? undefined);
  return (
    <Link
      to="/tutor/cards/$cardId"
      params={{ cardId: card.id }}
      className="hover:bg-ink-50 group flex min-h-[2.75rem] min-w-0 flex-1 items-center gap-3 rounded-xl p-3 transition-colors active:scale-[0.99]"
    >
      <div className="bg-primary-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
        <Layers className="text-primary-500 h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-ink-900 truncate text-[14px] font-semibold">
          <MathText>{card.title}</MathText>
        </p>
        <p className="text-ink-500 mt-0.5 truncate text-xs">
          {CARD_TYPE_LABELS[card.card_type]}
          {card.levels.length > 0 && ` · ${formatCardLevels(card.levels)}`}
          {` · ${card.xp_max} HORA`}
        </p>
        {statsParts && statsParts.length > 0 && (
          <p data-testid="card-admin-stats" className="text-primary-600 mt-0.5 text-xs">
            {statsParts.join(' · ')}
          </p>
        )}
      </div>
      <span
        className={cn(
          'shrink-0 rounded-full px-2 py-0.5 text-xs font-bold uppercase',
          CARD_STATUS_STYLES[card.status],
        )}
      >
        {CARD_STATUS_LABELS[card.status]}
      </span>
      <ChevronRight className="text-ink-300 h-4 w-4 shrink-0 group-hover:translate-x-0.5" />
    </Link>
  );
}

/**
 * Route tuteur `/tutor/cards` — SKILLCARDS pédagogiques (cartes à jouer 63 × 88 + QR). Chaque
 * carte est un outil atomique (méthode, formule, tableau…) dont le recto est
 * généré par l'IA à partir du savoir du tuteur, puis relu, publié et imprimé.
 */
/** Champs cherchables d'une carte de l'auteur (titre, domaine, sujet, règle, méthode). */
export function toCardFilterable(c: CardRow): CardFilterable {
  return {
    title: c.title,
    levels: c.levels,
    domainLabel: c.domain_label,
    extra: [c.topic, c.rule, c.method, c.when_to_use],
  };
}

export function TutorCardsPage() {
  const cards = useCards();
  const { user, profile } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const [status, setStatus] = useState<CardStatus | 'all'>('all');
  const adminStats = useAdminCardStats(admin);
  const [sort, setSort] = useState<AdminCardSort>('default');

  const [subject, setSubject] = useState<string>('');
  const [filters, setFilters] = useState<CardFilters>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const bulk = useBulkCardAction();
  const subjects = useMemo(() => {
    const present = new Set((cards.data ?? []).map((c) => c.subject));
    return SUBJECTS.filter((s) => present.has(s.value));
  }, [cards.data]);
  const byStatus = useMemo(
    () =>
      (cards.data ?? []).filter(
        (c) => (status === 'all' || c.status === status) && (!subject || c.subject === subject),
      ),
    [cards.data, status, subject],
  );
  const visible = useMemo(
    () => sortCardsByStats(filterCards(byStatus, toCardFilterable, filters), adminStats.data, sort),
    [byStatus, filters, adminStats.data, sort],
  );
  const { items: pageCards, paginator } = usePagedList(visible, {
    resetKeys: [status, subject, filters.q, filters.level, filters.domain, sort],
    unit: 'cartes',
    label: 'Pages des cartes',
  });

  const visibleIds = useMemo(() => visible.map((c) => c.id), [visible]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const selectedCards = useMemo(
    () => (cards.data ?? []).filter((c) => selected.has(c.id)),
    [cards.data, selected],
  );

  async function runBulk(action: BulkCardAction) {
    const plan = planBulkCardAction(
      action,
      selectedCards.map((c) => ({
        id: c.id,
        status: c.status,
        sectionCount: c.structured_content.sections.length,
        levelCount: c.levels.length,
        publishedVersion: c.published_version,
      })),
    );
    if (plan.targets.length === 0) {
      setSummary(formatBulkSummary(action, 0, plan.skipped));
      return;
    }
    if (
      (action === 'publish' || action === 'archive') &&
      !window.confirm(
        `${BULK_CARD_ACTION_LABELS[action]} ${plan.targets.length} carte(s) ?` +
          (action === 'publish' ? ' Elles deviendront visibles des élèves.' : ''),
      )
    ) {
      return;
    }
    setSummary(null);
    const result = await bulk.mutateAsync({
      targets: plan.targets,
      publish: action === 'publish',
      onProgress: (n, total) => setProgress(`${n} / ${total}`),
    });
    setProgress(null);
    setSummary(formatBulkSummary(action, result.done, plan.skipped, result.failed));
    setSelected(new Set());
  }

  return (
    <div
      className={cn(
        'mx-auto max-w-3xl px-4 py-6 sm:py-8',
        selected.size > 0
          ? 'pb-[calc(env(safe-area-inset-bottom)+11rem)] sm:pb-[calc(env(safe-area-inset-bottom)+6rem)]'
          : 'pb-[max(env(safe-area-inset-bottom),1.5rem)]',
      )}
    >
      <TutorLibraryTabs />
      <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-xl">
            <Layers className="text-primary-500 h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
              SKILLCARDS
            </h1>
            <p className="text-ink-500 text-xs">
              La carte de compétence à collectionner et maîtriser : une carte 63 × 88 à imprimer,
              reliée à l’app par QR code
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {admin && (
            <Link to="/tutor/cards/ideas" className="w-full sm:w-auto">
              <Button fullWidth variant="outline">
                <span className="flex items-center justify-center gap-2">
                  <Lightbulb className="h-4 w-4" /> Idées (IA)
                </span>
              </Button>
            </Link>
          )}
          <Link to="/tutor/cards/new" className="w-full sm:w-auto">
            <Button fullWidth>
              <span className="flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" /> Créer une carte
              </span>
            </Button>
          </Link>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Filtrer par statut">
        {(['all', ...CARD_STATUSES] as const).map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
            className={cn(
              'min-h-[2.75rem] rounded-full border px-3 text-sm font-semibold active:scale-[0.98]',
              status === s
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-ink-200 text-ink-600 bg-white',
            )}
          >
            {s === 'all' ? 'Toutes' : CARD_STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {subjects.length > 1 && (
        <label className="mb-2 block">
          <span className="sr-only">Matière</span>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="border-ink-200 text-ink-800 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border bg-white px-3 text-sm focus:outline-none"
          >
            <option value="">Toutes les matières</option>
            {subjects.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="mb-4">
        <SkillcardFilters
          cards={(cards.data ?? []).map(toCardFilterable)}
          value={filters}
          onChange={setFilters}
        />
      </div>

      {admin && (
        <label className="mb-4 block">
          <span className="text-ink-600 mb-1 block text-xs font-semibold">
            Trier par popularité (admin)
          </span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as AdminCardSort)}
            className="border-ink-200 text-ink-800 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border bg-white px-3 text-sm focus:outline-none"
          >
            {ADMIN_CARD_SORTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {cards.isLoading && <p className="text-ink-500 text-sm">Chargement…</p>}
      {cards.error && (
        <p role="alert" className="text-accent-600 text-sm">
          Impossible de charger les cartes : {cards.error.message}
        </p>
      )}

      {cards.data && visible.length === 0 && (
        <div className="border-ink-200 text-ink-500 rounded-2xl border border-dashed bg-white p-6 text-center text-sm">
          {cards.data.length === 0
            ? 'Aucune carte pour l’instant. Crée la première à partir d’une méthode ou d’une règle.'
            : 'Aucune carte ne correspond à ta recherche ou à tes filtres.'}
        </div>
      )}

      {summary && (
        <p role="status" className="bg-xp-50 text-xp-700 mb-3 rounded-xl px-3 py-2 text-sm">
          {summary}
        </p>
      )}

      {visible.length > 0 && (
        <div className="mb-1 flex items-center gap-1">
          <label className="flex h-11 cursor-pointer items-center gap-3 pl-3 pr-2">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={() => setSelected((sel) => toggleAll(sel, visibleIds))}
              className="accent-primary-500 h-5 w-5"
            />
            <span className="text-ink-700 text-sm font-semibold">
              Tout sélectionner ({visible.length})
            </span>
          </label>
          {selected.size > 0 && (
            <span className="text-ink-500 text-xs">{selected.size} sélectionnée(s)</span>
          )}
        </div>
      )}

      <div className="space-y-1">
        {pageCards.map((card) => (
          <CardListRow
            key={card.id}
            card={card}
            selected={selected.has(card.id)}
            onToggle={() => setSelected((sel) => toggleId(sel, card.id))}
            stats={admin ? (adminStats.data?.get(card.id) ?? null) : undefined}
          />
        ))}
      </div>
      <Paginator {...paginator} />

      {selected.size > 0 && (
        <div
          role="region"
          aria-label="Actions groupées"
          className="border-ink-200 ocean:bg-[#061433] fixed inset-x-0 bottom-0 z-30 border-t bg-white pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 shadow-lg"
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-ink-900 text-sm font-semibold">
                {selected.size} carte(s) sélectionnée(s)
                {progress && <span className="text-ink-500 font-normal"> — {progress}</span>}
              </p>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                aria-label="Vider la sélection"
                className="text-ink-500 hover:text-ink-800 flex h-11 w-11 items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              {(['review', 'publish', 'unreview', 'archive', 'restore'] as const).map((a) => (
                <Button
                  key={a}
                  variant={a === 'publish' ? 'primary' : 'outline'}
                  disabled={bulk.isPending}
                  onClick={() => void runBulk(a)}
                  className="min-h-[2.75rem] active:scale-[0.98]"
                >
                  {BULK_CARD_ACTION_LABELS[a]}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
